import angular from 'angular';

class InitBindController {
  static $inject = ['$rootScope', '$compile', '$sce'];

  /** @ngInject */
  constructor($rootScope, $compile, $sce) {
    Object.assign(this, {
      $rootScope,
      $compile,
      $sce
    });
  }
}

class InitBindDirective {

  constructor() {
    this.restrict = "A";
    this.controller = InitBindController;
    // Crucial for $ctrl access: NO isolated scope or `scope: true` for prototypical inheritance.
    // By omitting `this.scope`, the directive will share the parent scope by default,
    // which allows access to `$ctrl.page` and `$ctrl.config`.
    // this.scope = true; // Alternatively, use this for prototypical inheritance
  }

  link(scope, element, attrs, ctrl) {
    // Determine if the attribute is used as a boolean flag or with an expression value.
    // attrs.initBind will be an empty string if it's a boolean attribute (e.g., <div init-bind>).
    // attrs.initBind will be the expression string if it has a value (e.g., <div init-bind="$ctrl.body">).
    const isStaticContentScenario = (attrs.initBind !== undefined && attrs.initBind === '');

    /**
     * Helper function to compile and update the element's content.
     * This ensures consistency for both static and dynamic scenarios.
     * @param {string|Object} htmlValue The HTML string or an SCE trusted value object.
     */
    const compileAndRender = (htmlValue) => {
      // Clear any existing content to prevent duplication on updates or initial render
      element.empty();

      if (!htmlValue) {
        // No HTML to render, so just clear and return.
        return;
      }

      let actualHtmlString = htmlValue;

      // Handle $sce.trustAsHtml() output if present
      if (typeof htmlValue === 'object' && htmlValue.$$unwrapTrustedValue) {
        actualHtmlString = ctrl.$sce.getTrustedHtml(htmlValue);
      }

      // Set the HTML content first. This inserts the raw HTML into the DOM.
      element.html(actualHtmlString);

      // Crucial: Defer compilation to the next digest cycle using $rootScope.$evalAsync.
      // This ensures the DOM has been updated by the browser before AngularJS tries to compile its directives.
      ctrl.$rootScope.$evalAsync(() => {
        try {
          // Compile the *newly inserted content* (children of the element).
          // Pass the current `scope` from the link function, which has access to $ctrl from the parent.
          const compiledContent = ctrl.$compile(element.contents())(scope);

          if (!compiledContent || compiledContent.length === 0) {
            console.warn('InitBind: Compiled HTML resulted in no valid DOM nodes. Check HTML content or bindings.', actualHtmlString);
          }
        } catch (e) {
          console.error('InitBind: Error during compilation of dynamic HTML:', e, 'HTML:', actualHtmlString);
          element.empty(); // Clear content on compilation error
        }
      });
    };


    if (isStaticContentScenario) {
      // Scenario 1: <div init-bind>...</div> (Static content that's already inside the div in the HTML)
      // Get the initial inner HTML content of the directive's element.
      const initialInnerHtml = element.html();

      if (initialInnerHtml) {
        // Immediately compile and render this static content.
        // It's processed once when the directive links.
        compileAndRender(initialInnerHtml);
        console.log('InitBind: Processed static inner HTML content.');
      }

    } else {
      // Scenario 2: <div init-bind="someExpressionOrString"></div> (Dynamic HTML from an expression)
      // Watch the expression provided in the `init-bind` attribute.
      // The `scope.$watch` will trigger whenever the value of `attrs.initBind` changes.
      scope.$watch(attrs.initBind, function (newHtml) {
        // Compile and render the new dynamic HTML value.
        compileAndRender(newHtml);
        console.log('InitBind: Watched expression for dynamic HTML content changed.');
      });
    }

    // Optional: Cleanup on scope destroy if necessary, though $compile handles most resources.
    // scope.$on('$destroy', function() { /* ... */ });
  }
}

export default angular
  .module('initBind.directive', []) // Ensure this module name is correct and is imported in your app
  .directive('initBind', () => new InitBindDirective())
  .name;